import type { DriveFileRef } from "./types";

const DRIVE_API_BASE = "https://www.googleapis.com/drive/v3/files";

/** Native Google Workspace types have no raw bytes; export them instead of downloading. */
const NATIVE_GOOGLE_APP_MIME_TYPES = new Set([
  "application/vnd.google-apps.document",
  "application/vnd.google-apps.spreadsheet",
  "application/vnd.google-apps.presentation",
]);

function isNativeGoogleAppFile(mimeType: string | undefined): boolean {
  return Boolean(mimeType && NATIVE_GOOGLE_APP_MIME_TYPES.has(mimeType));
}

function withPdfExtension(name: string): string {
  return /\.pdf$/i.test(name) ? name : `${name}.pdf`;
}

/**
 * Downloads a picked Drive file's bytes client-side using the short-lived Picker access
 * token, and wraps it as a browser File so it can flow through the same code path as a
 * locally-picked file (image upload or document data-URL attachment).
 *
 * Native Google Docs/Sheets/Slides have no raw bytes, so they're exported to PDF instead.
 */
export async function downloadDriveFileAsFile(
  ref: DriveFileRef,
  accessToken: string
): Promise<File> {
  const exportAsPdf = isNativeGoogleAppFile(ref.mimeType);

  const url = exportAsPdf
    ? `${DRIVE_API_BASE}/${encodeURIComponent(ref.id)}/export?mimeType=${encodeURIComponent("application/pdf")}`
    : `${DRIVE_API_BASE}/${encodeURIComponent(ref.id)}?alt=media`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to download "${ref.name}" from Google Drive (${response.status})`
    );
  }

  const blob = await response.blob();
  const fileName = exportAsPdf ? withPdfExtension(ref.name) : ref.name;
  const mimeType = exportAsPdf
    ? "application/pdf"
    : ref.mimeType || blob.type || "application/octet-stream";

  return new File([blob], fileName, { type: mimeType });
}
